/**
 * bz-carousel-init.js
 * Embla 8.6.0 carousel initialiser — customer stories + medical professionals.
 * Extracted from adhd-supplements-guide.js to make carousel component self-contained.
 * Loaded by bz-component-carousel.liquid snippet.
 * Created: 2026-05-02 — Carousel component extraction
 */

/* ─────────────────────────────────────────────────────────────────────
   4b. EMBLA DUAL CAROUSEL — customer stories + medical professionals
   Replaced Splide 4.1.4 on 2026-04-23 due to persistent iOS Safari
   snap-back bug (swipe right → jumps back to slide 1) that 4 rounds of
   Splide config tuning could not resolve. Embla 8.6.0 uses native pointer
   event physics and avoids the drag-threshold / flickPower interaction
   that caused Splide's snap-back on iOS.

   Responsive perPage via CSS flex-basis (no JS breakpoints needed):
     desktop (>1024px): ~33.33% each → 3 visible
     @1024px:           ~40% each    → 2.5 visible
     @768px:            ~50% each    → 2 visible
     @600px:            ~83.33% each → 1.2 visible
   ───────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // ── Localized chrome (page language, never hardcoded EN) ────────────
  // Carousel UI strings follow <html lang>, fall back to EN. UTF-8 (this
  // file is already UTF-8 — see the box-drawing comments). The init LOGIC
  // stays identical across every market theme; only the rendered label
  // localizes, so the shared file can still be deployed fleet-wide as-is.
  var BZ_LANG = (document.documentElement.lang || 'en').split('-')[0].toLowerCase();
  var BZ_CAROUSEL_I18N = {
    en: { readQuote: '❝ Read quote',       readFull: 'Read full quote',                close: 'Close quote',        starAlt: 'Rated 4.74 out of 5' },
    fr: { readQuote: '❝ Lire la citation', readFull: 'Lire la citation complète', close: 'Fermer la citation', starAlt: 'Note de 4,74 sur 5' },
    de: { readQuote: '❝ Zitat lesen',      readFull: 'Vollständiges Zitat lesen', close: 'Zitat schließen',    starAlt: 'Bewertung 4,74 von 5' }
  };
  var BZ_T = BZ_CAROUSEL_I18N[BZ_LANG] || BZ_CAROUSEL_I18N.en;

  // ── Shared Embla initialiser ────────────────────────────────────────
  function initEmblaCarousel(wrapEl) {
    if (!wrapEl) return null;
    // Dual-guard (parity with UK): legacy inline bespoke-carousel scripts set
    // dataset.inited; this shared init sets dataset.emblaInited. Honour both so
    // they're mutually exclusive during the transition (first wins; no double-init).
    if (wrapEl.dataset.emblaInited || wrapEl.dataset.inited) return null;
    wrapEl.dataset.emblaInited = '1';
    wrapEl.dataset.inited = '1';

    var viewport = wrapEl.querySelector('.embla__viewport');
    if (!viewport) return null;

    // Topic filter: a topic-scoped carousel (data-carousel-topic on the .bz-carousel) drops
    // slides whose data-topic is neither the page topic nor 'both'. Runs BEFORE Embla init so
    // slide counts/dots are correct. No-op on untagged carousels (medical, other LPs).
    var carouselTopic = wrapEl.dataset.carouselTopic;
    if (carouselTopic) {
      viewport.querySelectorAll('.embla__slide[data-topic]').forEach(function (li) {
        var t = li.getAttribute('data-topic');
        if (t && t !== 'both' && t !== carouselTopic && li.parentNode) li.parentNode.removeChild(li);
      });
    }

    var embla = EmblaCarousel(viewport, {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      containScroll: false,   // false required for partial slides + loop
      dragFree: true
    });

    // ── Arrows ─────────────────────────────────────────────────────────
    var prevBtn = wrapEl.querySelector('.embla__btn--prev');
    var nextBtn = wrapEl.querySelector('.embla__btn--next');
    if (prevBtn) prevBtn.addEventListener('click', function () { embla.scrollPrev(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { embla.scrollNext(); });

    // ── Pagination dots ─────────────────────────────────────────────────
    var dotsWrap = wrapEl.querySelector('.embla__dots');
    if (dotsWrap) {
      buildDots(embla, dotsWrap);
    }

    return embla;
  }

  function buildDots(embla, dotsWrap) {
    var snapCount = embla.scrollSnapList().length;
    dotsWrap.innerHTML = '';
    for (var i = 0; i < snapCount; i++) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'embla__dot';
      btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dotsWrap.appendChild(btn);
    }

    function updateDots() {
      var idx = embla.selectedScrollSnap();
      dotsWrap.querySelectorAll('.embla__dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
      });
    }

    embla.on('select', updateDots);
    embla.on('reInit', function () { buildDots(embla, dotsWrap); });
    updateDots();

    dotsWrap.querySelectorAll('.embla__dot').forEach(function (d, i) {
      d.addEventListener('click', function () { embla.scrollTo(i); });
    });
  }

  // ── Video controls (unchanged contract) ─────────────────────────────
  // No drag-guard: tested 2026-04-25 with embla.scrollProgress() threshold
  // 0.001 — iOS finger noise tripped it on every tap, suppressing all clicks.
  // The original "swipe-release fires click on play button" risk (Embla #174)
  // hasn't actually surfaced in our testing; the idempotency guard alone is
  // enough to protect against the double-bind toggle pathology.
  function attachVideoControls(containerEl) {
    containerEl.querySelectorAll('.bz-play-btn').forEach(function (btn) {
      if (btn.dataset.bzBound === '1') return;
      btn.dataset.bzBound = '1';
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.bz-video-wrap');
        var video = wrap && wrap.querySelector('.bz-slide-video');
        // T-e22618 (2026-09-05): a paused video had its src RELEASED by attachMediaRelease() to free its
        // media connection. Restore it here, synchronously, BEFORE the src check and BEFORE play() — no
        // async gap, so iOS keeps the user gesture. Seek back to where it was paused; an ENDED video
        // restarts from 0 (restoring at the end would make it re-end immediately).
        if (video && !video.getAttribute('src') && video.dataset.bzSrc) {
          video.setAttribute('src', video.dataset.bzSrc);
          var bzT = parseFloat(video.dataset.bzTime || '0');
          // One pending seek at a time: a restore interrupted by a pause (released again before metadata
          // arrived) must not leave an old seek listener that fires on a LATER restore (Codex plan QC).
          if (video._bzSeek) { video.removeEventListener('loadedmetadata', video._bzSeek); video._bzSeek = null; }
          if (bzT > 0.5) {
            video._bzSeek = function () { video._bzSeek = null; try { video.currentTime = bzT; } catch (e) {} };
            video.addEventListener('loadedmetadata', video._bzSeek, { once: true });
          }
        }
        if (!video || !video.getAttribute('src')) return;

        document.querySelectorAll('.bz-slide-video').forEach(function (v) {
          if (v !== video && !v.paused) {
            v.pause();
            v.muted = true;
            var b = v.closest('.bz-video-wrap') && v.closest('.bz-video-wrap').querySelector('.bz-play-btn');
            if (b) b.classList.remove('is-playing');
          }
        });

        if (video.paused) {
          video.muted = false;
          // .catch muted-retry: kept as safety net. iOS/Chrome occasionally
          // rejects the unmuted play() inside a fresh gesture (rare race
          // with media-element state). Without the retry, video silently
          // fails to play at all. With it, user gets muted playback as
          // fallback — better than nothing.
          video.play().catch(function () {
            video.muted = true;
            video.play().catch(function (err) {
              console.warn('[bz-carousel] play() rejected even muted:', err && err.name);
              btn.classList.remove('is-playing');
            });
          });
          btn.classList.add('is-playing');
        } else {
          video.pause();
          btn.classList.remove('is-playing');
        }
      });
    });
  }

  // ── Media connection release (T-e22618, 2026-09-05) ─────────────────
  // MEASURED on the live Lane A page 2026-09-04: after three carousel videos had been played and paused,
  // the FOURTH one played stalled ~15 s whichever file it was — paused <video> elements keep their media
  // download open, so the per-host connection budget is exhausted by the 4th play (moov-atom position and
  // CDN latency were both refuted by data; the play-ORDER test proved it). Fix: ~300 ms after a pause, if
  // the video is still paused, remember src + position, drop src and load() — the connection is released
  // and the poster shows again. attachVideoControls() restores src on the next play tap (see there).
  // 'playing' stamps data-bz-frame on the wrap so CSS can hide the component's play circle while the
  // poster (which carries its own baked circle) is on screen — consumer CSS is a separate change.
  // Idempotent per video node via dataset.bzMediaBound: safe on reInit (same nodes). Embla 8 loop mode
  // does not clone slides (slide-node count == distinct video count on all four stores, measured
  // 2026-09-05). If a future engine ever cloned a bound node, cloneNode copies the marker but NOT the
  // listeners — clear the marker on clones and rebind; do not rely on this guard for them.
  function attachMediaRelease(containerEl) {
    if (!containerEl) return;
    containerEl.querySelectorAll('.bz-slide-video').forEach(function (v) {
      if (v.dataset.bzMediaBound === '1') return;
      v.dataset.bzMediaBound = '1';
      v.addEventListener('playing', function () {
        var w = v.closest('.bz-video-wrap');
        if (w) w.setAttribute('data-bz-frame', '1');
      });
      v.addEventListener('pause', function () {
        setTimeout(function () {
          var w = v.closest('.bz-video-wrap');
          if (w) w.removeAttribute('data-bz-frame');       // paused = poster/last frame: always clear the marker
          if (!v.paused || !v.getAttribute('src')) return;  // resumed within 300 ms, or already released
          if (v._bzSeek) { v.removeEventListener('loadedmetadata', v._bzSeek); v._bzSeek = null; }
          v.dataset.bzSrc = v.getAttribute('src');
          v.dataset.bzTime = v.ended ? '0' : String(v.currentTime || 0);
          v.removeAttribute('src');
          v.load();
        }, 300);
      });
    });
  }

  // ── First-frame thumbnail (unchanged contract) ───────────────────────
  function seekToFirstFrame(v) {
    if (v.readyState >= 1) {
      try { v.currentTime = 0.1; } catch (e) {}
    } else {
      v.addEventListener('loadedmetadata', function () {
        try { v.currentTime = 0.1; } catch (e) {}
      }, { once: true });
    }
    // NOTE: v.load() removed 2026-04-21 — aborts in-flight playback on cache-miss.
  }

  // ── All-viewport preload neutraliser (WS1 patch 2026-05-19) ──────────
  // PRIOR (Codex 5.5 fix 2026-05-04): only ran on desktop, on the assumption
  // that mobile Safari ignores preload="metadata" on cellular. PERF-DIAGNOSIS-v2
  // (2026-05-15) flagged that assumption as untested; with 8–34 carousel
  // videos on heavy pages (e.g. dopamine-guide x34), mobile connection
  // contention is real. Drop the desktop gate so preload="none" is forced
  // on every viewport. Posters remain rendered via <video poster> — that
  // attribute is independent of preload and serves a separate <img>.
  // querySelectorAll catches Embla loop clones too (slideNodes() only
  // returns originals).
  // SAFETY: only neutralise videos that have a poster. A posterless video
  // would otherwise lose its loadedmetadata event (preload=none prevents
  // metadata fetch), breaking seekToFirstFrame() which renders the
  // first-frame thumbnail. Today every Brainzyme carousel video has a
  // poster, but this guard means the function stays safe if that ever
  // changes (matches the seekToFirstFrame contract).
  function neutralizeVideoPreload(containerEl) {
    if (!containerEl) return;
    containerEl.querySelectorAll('.bz-slide-video').forEach(function (v) {
      if (!v.getAttribute('poster') && !v.getAttribute('data-poster')) return;
      if (v.preload !== 'none') {
        v.preload = 'none';
      }
    });
  }

  // ── Poster lazy-load (markup-level: data-poster -> poster near viewport) ──
  // preload="none" stops the VIDEO file but NOT its poster <img> (the poster
  // attribute fetches at parse, independent of preload). So posters ship as
  // data-poster (no eager fetch) and we promote them to a real poster= only
  // when the (below-fold) carousel nears the viewport. Same markup-level lesson
  // as neutralizeVideoPreload — a JS swap that ran unconditionally at init would
  // defeat the purpose. querySelectorAll catches Embla loop clones. Backward-
  // compatible: un-migrated markets ship a real poster= with no data-poster and
  // are untouched. Canon: reference/it-sops/canonical-landing-page-build.md §7.3b.
  function lazyLoadPosters(wrapEl) {
    if (!wrapEl) return;
    function promote() {
      wrapEl.querySelectorAll('.bz-slide-video[data-poster]').forEach(function (v) {
        v.setAttribute('poster', v.getAttribute('data-poster'));
        v.removeAttribute('data-poster');
      });
    }
    if (!('IntersectionObserver' in window)) { promote(); return; }
    var io = new IntersectionObserver(function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { promote(); obs.disconnect(); return; }
      }
    }, { rootMargin: '300px 0px' });
    io.observe(wrapEl);
  }

  // ── Medical quote reveal (canonical) ────────────────────────────────
  // Medical photo-only cards (no <video>) carry a CSS-hidden .bz-slide-quote.
  // Surface it on tap via a "Read quote" pill + full-card overlay. Behaviour
  // is market-agnostic; each store's own medical data snippet supplies the
  // experts + language. Idempotent via dataset.qok (safe with loop clones +
  // any legacy section-scoped copy).
  function attachQuoteReveal(containerEl) {
    if (!containerEl) return;
    containerEl.querySelectorAll('.bz-video-slide').forEach(function (slide) {
      if (slide.dataset.qok) return;
      var photo = slide.querySelector('.bz-photo-wrap');
      if (!photo) return;
      var q = slide.querySelector('.bz-slide-quote');
      if (!q || !q.textContent.trim()) return;
      slide.dataset.qok = '1';
      var attrib = slide.querySelector('.bz-slide-attrib');
      var nm = attrib ? attrib.querySelector('.bz-slide-name') : null;
      var rl = attrib ? attrib.querySelector('.bz-slide-role') : null;
      var attribHtml = '';
      if (nm) attribHtml += '<span class="bz-quote-ov__name">' + nm.innerHTML + '</span>';
      if (rl) attribHtml += '<span class="bz-quote-ov__role">' + rl.innerHTML + '</span>';
      if (!attribHtml && attrib) attribHtml = attrib.innerHTML;
      var ov = document.createElement('div');
      ov.className = 'bz-quote-ov';
      ov.innerHTML = '<button type="button" class="bz-quote-ov__close" aria-label="' + BZ_T.close + '">&times;</button>' +
                     '<p class="bz-quote-ov__text">' + q.innerHTML + '</p>' +
                     (attribHtml ? '<p class="bz-quote-ov__attrib">' + attribHtml + '</p>' : '');
      var hint = document.createElement('button');
      hint.type = 'button';
      hint.className = 'bz-quote-hint';
      hint.setAttribute('aria-label', BZ_T.readFull);
      hint.innerHTML = BZ_T.readQuote;
      photo.appendChild(ov);
      photo.appendChild(hint);
      function openOv() { ov.classList.add('is-open'); }
      function closeOv() { ov.classList.remove('is-open'); }
      hint.addEventListener('click', function (e) { e.stopPropagation(); openOv(); });
      ov.querySelector('.bz-quote-ov__close').addEventListener('click', function (e) { e.stopPropagation(); closeOv(); });
      photo.addEventListener('click', function () { ov.classList.contains('is-open') ? closeOv() : openOv(); });
    });
  }

  // ── Boot ─────────────────────────────────────────────────────────────
  function initCarousels() {
    if (typeof EmblaCarousel === 'undefined') return;

    var customerWrap = document.querySelector('.bz-carousel--customers');
    var medicalWrap  = document.querySelector('.bz-carousel--medical');

    var customerEmbla = initEmblaCarousel(customerWrap);
    var medicalEmbla  = initEmblaCarousel(medicalWrap);

    if (customerEmbla) {
      lazyLoadPosters(customerWrap);
      neutralizeVideoPreload(customerWrap);
      attachVideoControls(customerWrap);
      attachMediaRelease(customerWrap);
      customerEmbla.on('reInit', function () {
        neutralizeVideoPreload(customerWrap);
        attachVideoControls(customerWrap);
        attachMediaRelease(customerWrap);
      });
    }
    if (medicalEmbla) {
      lazyLoadPosters(medicalWrap);
      neutralizeVideoPreload(medicalWrap);
      attachVideoControls(medicalWrap);
      attachMediaRelease(medicalWrap);
      attachQuoteReveal(medicalWrap);
      medicalEmbla.on('reInit', function () {
        neutralizeVideoPreload(medicalWrap);
        attachVideoControls(medicalWrap);
        attachMediaRelease(medicalWrap);
        attachQuoteReveal(medicalWrap);
      });
    }

    // First-frame thumbnails — only for videos without a poster.
    // When poster= is set (all our videos), the browser shows the poster
    // image directly with no decode/seek needed. Calling currentTime=0.1
    // on a video that has a poster can replace the poster with a partial
    // / blank decode frame on iOS Chrome (root cause of "missing frames"
    // observed 2026-04-25).
    document.querySelectorAll('.bz-carousel--customers .bz-slide-video, .bz-carousel--medical .bz-slide-video').forEach(function (v) {
      if (!v.getAttribute('src')) return;
      if (v.getAttribute('poster') || v.getAttribute('data-poster')) return;
      seekToFirstFrame(v);
    });

    // ── Killer-LP bespoke carousels (lifestyle / selling / community) ──────
    // Same Embla config + dots as the customer/medical carousels — driven from
    // the shared initialiser instead of per-section inline scripts (parity with UK).
    ['.bz-lifecar', '.bz-imgcar', '.bz-commcar'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (w) { initEmblaCarousel(w); });
    });
    killerLPa11y();
  }

  // ── Killer-LP glue (parity with UK; was duplicated inline per section) ─────
  // a11y: demote the hardcoded role="tablist" on bespoke-carousel dots.
  function killerLPa11y() {
    document.querySelectorAll('.bz-lifecar .embla__dots[role="tablist"], .bz-imgcar .embla__dots[role="tablist"], .bz-commcar .embla__dots[role="tablist"]').forEach(function (d) {
      d.removeAttribute('role'); d.removeAttribute('aria-label');
    });
    // Re-assert role="list" on the bespoke-carousel <ul.embla__container>: Embla applies
    // display:flex + list-style:none which strips implicit list semantics in WebKit, leaving
    // the <li.embla__slide> as orphan listitems (axe listitem + aria-required-children FAIL on
    // every killer LP). Scope to the 3 bespoke carousels ONLY (each is <ul><li>); the
    // bz-component-carousel container is a <div> with no <li> and must stay role-less. T-09efa8.
    document.querySelectorAll('.bz-lifecar .embla__container, .bz-imgcar .embla__container, .bz-commcar .embla__container').forEach(function (c) {
      if (c.querySelector(':scope > .embla__slide') && c.getAttribute('role') !== 'list') {
        c.setAttribute('role', 'list');
      }
    });
  }
  // Closing-CTA shelf: clone the main shelf grid, strip the duration toggle.
  function fillCloseShelf() {
    var src = document.getElementById('shelfGrid');
    var dst = document.querySelector('.bz-close-shelf');
    if (!src || !dst || !src.children.length) return false;
    dst.innerHTML = src.innerHTML;
    dst.querySelectorAll('.shelf-duration').forEach(function (d) { d.remove(); });
    return true;
  }
  // Sticky CTA bar visibility + lift the chatbot above it when shown.
  function initSticky() {
    var el = document.getElementById('bzKSticky');
    if (!el || el.dataset.skBound) return;   // idempotent: safe if the JS is loaded twice
    el.dataset.skBound = '1';
    function chatPos() {
      var host = document.querySelector('n8nchatui-popup');
      if (!host || !host.shadowRoot) return;
      var st = host.shadowRoot.getElementById('bz-chat-pos');
      if (!st) { st = document.createElement('style'); st.id = 'bz-chat-pos'; host.shadowRoot.appendChild(st); }
      var up = el.classList.contains('is-visible');
      st.textContent = 'button.fixed{bottom:' + (up ? '92px' : '20px') + ' !important;transition:bottom .3s ease;}';
    }
    function on() { (window.scrollY > 700) ? el.classList.add('is-visible') : el.classList.remove('is-visible'); chatPos(); }
    window.addEventListener('scroll', on, { passive: true }); on();
    // Bounded poll (was a never-cleared setInterval - ran forever every 800ms).
    // Only needed to catch the async-loading chat host; stop once it's found +
    // positioned, or after ~32s. Scroll-driven chatPos() keeps it aligned after.
    var chatTries = 0;
    var chatTimer = setInterval(function () {
      chatPos();
      var host = document.querySelector('n8nchatui-popup');
      if ((host && host.shadowRoot) || ++chatTries > 40) clearInterval(chatTimer);
    }, 800);
  }
  // Replace glyph star ratings on the shelf with the green verified-stars PNG.
  function fixStars() {
    var SRC = 'https://cdn.shopify.com/s/files/1/1719/2651/files/48_ReviewStars_green.png?v=1759414851&width=160';
    document.querySelectorAll('.shelf-rating .stars, .shelf-review .stars').forEach(function (el) {
      if (el.dataset.imgified) return; el.dataset.imgified = '1';
      // 2026-07-28 (T-ad977a) fixed the alt on UK only; 2026-09-05 (T-c53c23): US and DE still carried the
      // French "Note de 4,74 sur 5" on every shelf-rating image. The alt now comes from BZ_CAROUSEL_I18N via
      // BZ_LANG (document.documentElement.lang), so ONE shared file renders the right language per store.
      el.innerHTML = '<img src="' + SRC + '" alt="' + BZ_T.starAlt + '" style="height:13px;width:auto;display:inline-block;vertical-align:middle;">';
    });
  }
  function initKillerGlue() {
    if (!document.querySelector('.bz-ksticky, .bz-close-shelf, .bz-lifecar')) return; // killer LP only
    initSticky();
    var n = 0, iv = setInterval(function () { if (fillCloseShelf() || ++n > 50) clearInterval(iv); }, 150);
    if (document.readyState !== 'loading') fillCloseShelf();
    var c = 0, sv = setInterval(function () { fixStars(); killerLPa11y(); if (++c > 60) clearInterval(sv); }, 200);
    if (document.readyState !== 'loading') fixStars();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initKillerGlue);
  else initKillerGlue();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    if (typeof EmblaCarousel !== 'undefined') {
      initCarousels();
    } else {
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        if (typeof EmblaCarousel !== 'undefined') { clearInterval(poll); initCarousels(); }
        else if (attempts > 20) { clearInterval(poll); }
      }, 100);
    }
  }
})();
